import { useRef, useState } from "preact/hooks";

import { useModalA11y } from "../hooks/useModalA11y";
import { toISODate, todayLocal } from "../lib/calendarDates";
import {
  editHealthNoteSheetTitle,
  HEALTH_EVENT_TYPE_OPTIONS,
  SAVE_CHANGES_LABEL,
} from "../lib/recordsCopy";
import type { HealthEventTypeValue } from "../types";

export interface AddHealthNoteSheetProps {
  open: boolean;
  animalName: string;
  /** Sprint-010 C4 (design-phase-c.md §2.4) — `"add"` (default, unchanged
   * from sprint-005 C4) or `"edit"`, the Care & health tab's per-item edit
   * sheet. Edit mode pre-fills every field from the event being edited and
   * adds the one field create mode never shows: Date (§2.4's own
   * deliberate asymmetry — a correction-only need for imported/mis-dated
   * notes, not a fresh note logged today). */
  mode?: "add" | "edit";
  initialNote?: string;
  initialEventType?: HealthEventTypeValue | null;
  /** Required, ISO `event_date`, when `mode === "edit"`. */
  initialEventDate?: string;
  /** Required when `mode === "add"` (default). */
  onAdd?: (note: string, eventType: HealthEventTypeValue | null) => Promise<void>;
  /** Required when `mode === "edit"`. */
  onSave?: (
    note: string,
    eventType: HealthEventTypeValue | null,
    eventDate: string,
  ) => Promise<void>;
  onCancel: () => void;
}

/**
 * Sprint-005 C4 — the Animal detail screen's "Add a note" bottom sheet,
 * admin-only (the caller gates rendering on `isAdmin`; see
 * `AnimalDetailOverlay.tsx`). A freeform note (the one required field,
 * matching `HealthEventCreateIn.note`'s own min_length=1 contract) plus
 * optional plain-language type chips — never a dropdown of raw
 * `event_type` enum values.
 *
 * Same chrome/dismiss conventions as `AddPersonSheet`/`AssigneePickerSheet`
 * (backdrop click, Escape, Cancel all dismiss without saving) — and, per
 * sprint-005 C1's root-cause fix to `AddPersonSheet`, the form reset here
 * is applied SYNCHRONOUSLY during render (never inside a post-commit
 * `useEffect`) from day one: resetting via an effect left a real race
 * window where a fast fill-then-submit could have its input silently
 * wiped by the effect's own reset running afterward. See
 * `AddPersonSheet.tsx`'s comments for the full incident writeup this
 * component was built to avoid repeating.
 *
 * Sprint-010 C4 (design-phase-c.md §2.4) — extended with `mode: "add" |
 * "edit"` (§0's chosen mechanism: a sheet, not inline, for this
 * multi-field per-list-item edit). Edit mode is a SEPARATE mounted
 * instance from the create-mode sheet (see `AnimalDetailOverlay.tsx`'s
 * `CareHealthPanel`) — simpler than merging two different reset/pre-fill
 * shapes into one state machine.
 */
export function AddHealthNoteSheet({
  open,
  animalName,
  mode = "add",
  initialNote,
  initialEventType,
  initialEventDate,
  onAdd,
  onSave,
  onCancel,
}: AddHealthNoteSheetProps) {
  const todayIso = toISODate(todayLocal());
  const [note, setNote] = useState(mode === "edit" ? (initialNote ?? "") : "");
  const [eventType, setEventType] = useState<HealthEventTypeValue | null>(
    mode === "edit" ? (initialEventType ?? null) : null,
  );
  const [eventDate, setEventDate] = useState(initialEventDate ?? todayIso);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const noteRef = useRef<HTMLTextAreaElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Reset SYNCHRONOUSLY on the closed->open transition (see this
  // component's own docstring / AddPersonSheet.tsx's sprint-005 C1
  // writeup) — before anything paints, so there is no window for a fast
  // interaction to race the reset.
  const wasOpenRef = useRef(open);
  if (open && !wasOpenRef.current) {
    setNote(mode === "edit" ? (initialNote ?? "") : "");
    setEventType(mode === "edit" ? (initialEventType ?? null) : null);
    setEventDate(initialEventDate ?? todayIso);
    setError(null);
  }
  wasOpenRef.current = open;

  // Focus trap + Escape-to-cancel + focus-restore-on-close (sprint-006 D3).
  // Sprint-011 C1 (TD-012) — backdrop + Cancel route through the hook's
  // returned `close`, not `onCancel` directly.
  const { close } = useModalA11y(containerRef, {
    active: open,
    onClose: onCancel,
    initialFocusRef: noteRef,
  });

  if (!open) return null;

  async function submit() {
    if (!note.trim()) return;
    setSaving(true);
    setError(null);
    try {
      if (mode === "edit") {
        await onSave?.(note.trim(), eventType, eventDate);
      } else {
        await onAdd?.(note.trim(), eventType);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't save this note — try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <div class="assign-sheet-backdrop is-open" onClick={close} />
      <div
        ref={containerRef}
        class="assign-sheet is-open"
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-health-note-title"
      >
        <div class="assign-sheet-handle" aria-hidden="true" />
        <h2 class="assign-sheet-title" id="add-health-note-title">
          {mode === "edit" ? editHealthNoteSheetTitle(animalName) : `Add a note for ${animalName}`}
        </h2>

        <label class="admin-field-label" for="health-note-text">
          What happened
        </label>
        <textarea
          ref={noteRef}
          id="health-note-text"
          class="pick-input admin-textarea"
          placeholder="Trimmed hooves, gave dewormer, vet check…"
          value={note}
          required
          aria-required="true"
          onInput={(e) => setNote((e.target as HTMLTextAreaElement).value)}
        />

        <span class="admin-field-label">Kind (optional)</span>
        <div class="admin-day-picker" role="group" aria-label="Kind of note (optional)">
          {HEALTH_EVENT_TYPE_OPTIONS.map((option) => {
            const isSelected = eventType === option.value;
            return (
              <button
                key={option.value}
                type="button"
                class={`admin-day-chip${isSelected ? " is-selected" : ""}`}
                aria-pressed={isSelected}
                onClick={() => setEventType(isSelected ? null : option.value)}
              >
                {option.label}
              </button>
            );
          })}
        </div>

        {mode === "edit" ? (
          <>
            <label class="admin-field-label" for="health-note-date">
              Date
            </label>
            <input
              id="health-note-date"
              type="date"
              class="pick-input"
              max={todayIso}
              value={eventDate}
              required
              aria-required="true"
              onInput={(e) => setEventDate((e.target as HTMLInputElement).value)}
            />
          </>
        ) : null}

        {error ? (
          <p class="error-hint" role="alert">
            {error}
          </p>
        ) : null}

        <button
          type="button"
          class="primary-btn"
          style={{ marginTop: "14px" }}
          disabled={!note.trim() || saving}
          onClick={() => void submit()}
        >
          {saving ? "Saving…" : mode === "edit" ? SAVE_CHANGES_LABEL : "Save note"}
        </button>
        <button type="button" class="secondary-btn" style={{ marginTop: "10px" }} onClick={close}>
          Cancel
        </button>
      </div>
    </>
  );
}
